import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Server } from 'lucide-react';
import toast from '../design-system/toast';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { ScanCreateRequest } from '../types/scan';
import ApplicationForm, { ApplicationFormSubmitMeta } from '../components/applications/ApplicationForm';
import PostHogService, { OnboardingEvent } from '../posthog';
import OnboardingProgress from '../components/onboarding/OnboardingProgress';
import { Spinner } from '../design-system';

const OnboardingApplicationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization, isLoading: orgLoading, refreshOrganizations } = useOrganization();
  const api = useAuthenticatedApi();
  const hasTrackedViewed = useRef(false);

  const organizationId = currentOrganization?.id;

  // Track onboarding step view once per mount (StrictMode-safe)
  useEffect(() => {
    if (!organizationId || hasTrackedViewed.current) return;
    hasTrackedViewed.current = true;
    PostHogService.track(OnboardingEvent.APP_VIEWED, {
      organization_id: organizationId,
    });
  }, [organizationId]);

  // Handle OAuth callback query parameters for git provider connection.
  // After successful OAuth, users are redirected here (not back to /onboarding/connect),
  // so this is where we fire the git_connected / git_connect_failed events.
  useEffect(() => {
    const provider = searchParams.get('provider');
    const status = searchParams.get('status');
    const error = searchParams.get('error');

    if (provider && status === 'connected') {
      const providerName = provider === 'gitlab' ? 'GitLab' : provider === 'github' ? 'GitHub' : provider;
      toast.success(`${providerName} connected successfully!`);
      PostHogService.track(OnboardingEvent.GIT_CONNECTED, {
        provider,
        organization_id: organizationId,
      });
      searchParams.delete('provider');
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (error) {
      const providerName = provider === 'gitlab' ? 'GitLab' : provider === 'github' ? 'GitHub' : null;
      if (error === 'oauth_state_expired' || error === 'oauth_state_invalid') {
        toast.error('Connection expired. Please try again.');
      } else if (error === 'oauth_invalid_response') {
        toast.error(`${providerName || 'Provider'} returned an invalid response. Please try again.`);
      } else if (error === 'oauth_invalid_user_info') {
        toast.error(`Failed to retrieve ${providerName || 'provider'} user information. Please try again.`);
      } else if (error === 'oauth_connection_failed' && providerName) {
        toast.error(`Failed to connect ${providerName}. Please try again.`);
      } else {
        toast.error('Connection failed. Please try again.');
      }
      PostHogService.track(OnboardingEvent.GIT_CONNECT_FAILED, {
        provider: provider ?? 'unknown',
        organization_id: organizationId,
        error_code: error,
      });
      searchParams.delete('provider');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, organizationId]);

  // Fire the first scan as soon as the repository is selected. Deliberately not
  // awaited: ApplicationForm re-enables its submit button right after onSuccess, so
  // we navigate away in the same tick to avoid a duplicate-application submit. The
  // overview picks up the running scan via polling (useRepositoryScanProgress).
  const startFirstScan = (applicationId: string) => {
    const requestData: ScanCreateRequest = {
      application_id: applicationId,
      scan_type: 'deep_ai_scan',
      source: 'onboarding',
    };
    api
      .post(apiEndpoints.scans.create(), requestData)
      .then((response) => {
        PostHogService.track(OnboardingEvent.SCAN_STARTED, {
          organization_id: organizationId,
          application_id: applicationId,
          scan_batch_id: response?.batch_id,
          scan_type: 'deep_ai_scan',
          source: 'onboarding',
        });
        // Refresh org access/onboarding state now that the first scan is queued.
        void refreshOrganizations().catch((refreshError) => {
          console.warn('Failed to refresh organizations after onboarding scan start:', refreshError);
        });
      })
      .catch((error: any) => {
        console.error('Error auto-starting first scan:', error);
        const reason = error?.message || error?.data?.detail || 'Failed to start scan';
        PostHogService.track(OnboardingEvent.SCAN_START_FAILED, {
          organization_id: organizationId,
          application_id: applicationId,
          reason,
        });
        toast.error('We could not start your scan automatically. You can run it from the overview.');
      });
  };

  const handleSuccess = (application: { id: string }, meta: ApplicationFormSubmitMeta) => {
    PostHogService.track(OnboardingEvent.APP_CREATED, {
      organization_id: organizationId,
      application_id: application.id,
      repo_count: meta.repoCount,
      repo_providers: meta.repoProviders,
      private_repo_count: meta.privateRepoCount,
      has_description: meta.hasDescription,
      source: 'onboarding',
    });

    startFirstScan(application.id);
    navigate(`/repositories/${application.id}`, { replace: true, state: { firstScan: true } });
  };

  const handleError = (err: unknown, meta: ApplicationFormSubmitMeta) => {
    const error = err as { data?: { detail?: string }; message?: string };
    const reason = error?.data?.detail || error?.message || 'unknown_error';
    PostHogService.track(OnboardingEvent.APP_CREATE_FAILED, {
      organization_id: organizationId,
      reason,
      repo_count: meta.repoCount,
      repo_providers: meta.repoProviders,
      private_repo_count: meta.privateRepoCount,
      has_description: meta.hasDescription,
      source: 'onboarding',
    });
  };

  if (orgLoading) {
    return (
      <div className="min-h-screen bg-ui-background-primary flex items-center justify-center">
        <Spinner size={32} />
      </div>
    );
  }

  if (!currentOrganization) {
    navigate('/organization-setup', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-ui-background-primary text-ui-text-primary flex flex-col">
      <OnboardingProgress currentStep={2} />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-brand-primary/10 dark:bg-brand-primary/20 rounded-full flex items-center justify-center">
              <Server className="w-8 h-8 text-brand-primary dark:text-brand-primaryDarkMode" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">Select Your First Repository</h1>
            <p className="text-ui-text-secondary">
              Choose the repository you want Kolega to monitor and scan.
            </p>
          </div>

          {/* Application Form */}
          <div className="bg-ui-background-secondary rounded-lg p-6 mb-6">
            <ApplicationForm
              organizationId={currentOrganization.id}
              onSuccess={handleSuccess}
              onError={handleError}
              submitLabel="Scan this repository"
              autoFocus={false}
              githubRedirectTo="/onboarding/application"
              variant="onboarding_repo_only"
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingApplicationPage;
